'use client';

import { RelativeURLBuilder } from '@bigcommerce/atom-utils';
import { clsx } from 'clsx';
import connectAutocomplete, {
  type AutocompleteConnectorParams,
  type AutocompleteWidgetDescription,
} from 'instantsearch.js/es/connectors/autocomplete/connectAutocomplete';
import debounce from 'lodash.debounce';
import { ArrowUpLeft, Search } from 'lucide-react';
import { useFormatter, useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Configure,
  // Highlight,
  Hits,
  Index,
  InstantSearch,
  useConnector,
} from 'react-instantsearch';

import { Input } from '@/vibes/mom/form/input';
import { Button } from '@/vibes/mom/primitives/button';
import { useSearchOverlay } from '@/vibes/mom/primitives/navigation/hooks/use-navigation-overlay';
import { Rating } from '@/vibes/mom/primitives/rating';
import { RecordQuery } from '~/components/algolia/record-query';
import { Link } from '~/components/link';
import { usePathname, useRouter } from '~/i18n/routing';
import { algoliaFutureSettings } from '~/lib/algolia/config';
import { mapToIProductModel } from '~/lib/algolia/map-to-product-model';
import { algoliaFieldNames } from '~/lib/algolia/schema';
import { env } from '~/lib/atom/env';
import { PriceConfig } from '~/lib/atom/types/price-config';

import ErrorBoundary from '../error-boundary';

import { customSearchClient } from './custom-search-client';

// Types for query suggestions
interface QuerySuggestion {
  objectID: string;
  query: string;
}

function useAutocomplete(props?: AutocompleteConnectorParams) {
  return useConnector<AutocompleteConnectorParams, AutocompleteWidgetDescription>(
    connectAutocomplete,
    props,
  );
}

const AutocompleteSearch = ({
  priceConfig,
  isLoggedIn,
}: {
  priceConfig: PriceConfig;
  isLoggedIn?: boolean;
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedRefineRef = useRef<((value: string) => void) | null>(null);

  const format = useFormatter();
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('Components.Header');

  // Use the autocomplete connector for main search
  const { indices, currentRefinement, refine } = useAutocomplete();

  const [inputValue, setInputValue] = useState(currentRefinement);
  const [isSearching, setIsSearching] = useState(false);

  const showResults = currentRefinement.length > 0;

  useSearchOverlay(isDropdownOpen, showResults);

  // Get query suggestions from the second index with useMemo
  const querySuggestions = useMemo<QuerySuggestion[]>(() => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (indices[1]?.hits as QuerySuggestion[] | undefined) ?? [];
  }, [indices]);

  const productsLength = indices[0]?.hits.length || 0;

  // Update total items count for keyboard navigation
  const totalItems = useMemo(() => {
    return querySuggestions.length + productsLength + 1; // +1 for "View More"
  }, [querySuggestions.length, productsLength]);

  const handleLinkClick = () => {
    setIsDropdownOpen(false);
    setInputValue('');
  };

  const handleSearchSubmit = () => {
    const query = inputValue.trim();

    if (query !== '') {
      router.push(`/search?query=${encodeURIComponent(query)}`);
      setIsDropdownOpen(false);
      setInputValue('');
    }
  };

  // Updated handleKeyDown
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (selectedIndex !== null && e.key === 'Enter') {
        if (selectedIndex < querySuggestions.length) {
          // Handle suggestion selection
          const selectedSuggestion = querySuggestions[selectedIndex];

          if (selectedSuggestion && typeof selectedSuggestion.query === 'string') {
            setInputValue(selectedSuggestion.query);
            refine(selectedSuggestion.query);
          }
        } else if (selectedIndex < querySuggestions.length + productsLength) {
          // Handle product selection
          const productIndex = selectedIndex - querySuggestions.length;
          const selectedProduct = indices[0]?.hits[productIndex];

          if (selectedProduct?.url && selectedProduct.sku) {
            const url = RelativeURLBuilder.Url(String(selectedProduct.url))
              .setQueryParam('sku', String(selectedProduct.sku))
              .toString();

            router.push(url);
          }

          setIsDropdownOpen(false);
          setInputValue('');
        } else {
          // Handle "View More Results"
          router.push(`/search?query=${encodeURIComponent(inputValue)}`);
          setInputValue('');
        }
      } else {
        handleSearchSubmit();
      }
    } else if (e.key === 'ArrowDown') {
      setSelectedIndex((prev) => (prev === null || prev === totalItems - 1 ? 0 : prev + 1));
    } else if (e.key === 'ArrowUp') {
      setSelectedIndex((prev) => (prev === null || prev === 0 ? totalItems - 1 : prev - 1));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);

    if (e.target.value.length > 0) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }

    debouncedRefineRef.current?.(e.target.value);
  };

  const handleEscapeKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') setIsDropdownOpen(false);
  }, []);

  // Close dropdown when pressing escape key
  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey);

    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [handleEscapeKey]);

  useEffect(() => {
    if (currentRefinement.length > 0 && indices.length > 0) {
      setIsSearching(false);
    }
  }, [currentRefinement, indices]);

  // Open dropdown when results are available
  useEffect(() => {
    if (showResults && !isSearching) {
      // Small delay to prevent race conditions
      setTimeout(() => setIsDropdownOpen(true), 10);
    } else if (!showResults) {
      setIsDropdownOpen(false);
    }
  }, [showResults, isSearching]);

  // Close dropdown when clicking outside div
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown when navigating to a new page
  useEffect(() => {
    setIsDropdownOpen(false);
    refine('');
  }, [pathname, refine]);

  useEffect(() => {
    debouncedRefineRef.current = debounce((value: string) => {
      refine(value);
    }, 300);

    return () => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      (debouncedRefineRef.current as ReturnType<typeof debounce>).cancel();
    };
  }, [refine]);

  const hideTradePrices = env.NEXT_PUBLIC_SITE_TYPE === 'B2B' && !isLoggedIn;

  return (
    <div className="relative mx-auto w-full max-w-[765px]" id="search-dropdown" ref={dropdownRef}>
      <div className="relative">
        <Input
          className="[&>input:focus]:bg-mom-warmWhite w-full text-mom-black [&>input:focus-visible]:border-gray-200 [&>input:focus-visible]:bg-[#F4F1E8] [&>input:hover]:border-gray-200 [&>input]:rounded md:[&>input]:rounded-3xl"
          onChange={handleInputChange}
          onFocus={() => showResults && setIsDropdownOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('Search.placeholder')}
          ref={inputRef}
          type="text"
          value={inputValue}
        />
        <Button
          className="!absolute !right-1 !top-0 !h-full !w-12 !p-4 hover:!border-0"
          onClick={handleSearchSubmit}
          variant="ghost"
        >
          <Search className="p-0! h-[47px] text-black hover:!bg-transparent" />
        </Button>
      </div>

      {isDropdownOpen && (
        <div className="absolute left-0 z-20 mt-1 flex w-full flex-col rounded-lg border bg-white p-2 shadow-lg">
          <ul>
            {querySuggestions.length > 0 && (
              <>
                <span className="p-2 font-heading-condensed font-semibold uppercase tracking-wide text-mom-gold">
                  {t('Search.suggestions')}
                </span>
                {querySuggestions.map((suggestion, index) => {
                  if (typeof suggestion.query === 'string') {
                    return (
                      <li
                        className={clsx(
                          'cursor-pointer rounded px-2 py-1 text-mom-black hover:bg-[#F4F1E8]',
                          selectedIndex === index && 'rounded bg-[#F4F1E8]',
                        )}
                        key={suggestion.query}
                      >
                        <Link
                          className="flex items-center gap-2 text-mom-black"
                          href={`/search?query=${encodeURIComponent(suggestion.query)}`}
                          onClick={handleLinkClick}
                        >
                          <Search className="h-[42px] !p-0 text-gray-500" />
                          <span>{suggestion.query}</span>
                          <ArrowUpLeft className="ml-auto h-[42px] !p-0 text-gray-400" />
                        </Link>
                      </li>
                    );
                  }

                  return null;
                })}
              </>
            )}
            {querySuggestions.length > 0 && productsLength > 0 && <hr className="my-2" />}

            {/* Products */}
            {productsLength > 0 ? (
              <>
                <span className="p-2 font-heading-condensed font-semibold uppercase tracking-wide text-mom-gold">
                  {t('Search.products')}
                </span>
                <RecordQuery />
                <Hits
                  classNames={{
                    list: 'flex flex-col gap-2',
                    item: '',
                  }}
                  hitComponent={({ hit: rawHit }) => {
                    const hit = {
                      ...rawHit,
                      ...mapToIProductModel(rawHit, priceConfig),
                    };

                    // eslint-disable-next-line no-underscore-dangle
                    const itemIndex = querySuggestions.length + (hit.__position - 1);

                    return (
                      <Link
                        className={clsx(
                          'flex w-full cursor-pointer gap-3 rounded p-2 text-mom-black hover:bg-[#F4F1E8]',
                          selectedIndex === itemIndex && 'bg-[#F4F1E8]',
                        )}
                        href={hit.urlRelative}
                        onClick={handleLinkClick}
                      >
                        <div className="flex h-[76px] w-[70px] flex-col items-center rounded bg-white p-1">
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            alt={hit.name || 'Product Image'}
                            className="h-full w-auto object-contain"
                            src={hit.imageUrlRelative}
                          />
                        </div>

                        <div className="flex w-full flex-col gap-1 text-gray-800">
                          <div className="flex flex-row justify-between gap-8">
                            {/* TODO: Use highlight component once product titles are transformed at Algolia level https://atom.atlassian.net/browse/REPL-1244 */}
                            {/* <Highlight
                              attribute="name"
                              classNames={{
                                root: 'text-mom-black',
                                highlighted: 'font-medium bg-[#E5E1D5]',
                              }}
                              highlightedTagName="span"
                              hit={hit}
                            /> */}
                            <span>{hit.name}</span>
                            {!hit.meta.hasBespokePrice &&
                              hit.price.current > 0 &&
                              !hideTradePrices && (
                                <div className="text-sm">
                                  {format.number(hit.price.current, {
                                    style: 'currency',
                                    currency: priceConfig.currencyCode,
                                  })}
                                </div>
                              )}
                          </div>
                          {hit.availability.inStock ? (
                            <span className="text-xs">{t('Search.inStock')}</span>
                          ) : (
                            <span className="text-xs">{t('Search.outOfStock')}</span>
                          )}
                          <div className="text-sm text-[#4C4C4C]">
                            {Boolean(hit.meta.volume) && `${hit.meta.volume} cl`}
                            {Boolean(hit.meta.volume) && Boolean(hit.meta.abv) && ' / '}
                            {Boolean(hit.meta.abv) && `${hit.meta.abv}% ABV`}
                          </div>
                          {hit.reviews.rating !== null && hit.reviews.rating > 0 && (
                            <Rating
                              className="[&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-mom-gold"
                              rating={hit.reviews.rating}
                              showRating={false}
                            />
                          )}
                        </div>
                      </Link>
                    );
                  }} // We don't need to render hits here, we do it manually below
                />
              </>
            ) : (
              <li className="p-2 text-gray-500">{t('Search.noSearchResultsFound')}</li>
            )}

            {(querySuggestions.length > 0 || productsLength > 0) && (
              <li
                className={clsx('flex cursor-pointer flex-col items-center justify-center gap-2')}
              >
                <hr className="mt-2 inline-flex w-full" />
                <Link
                  className={clsx(
                    'flex w-full cursor-pointer items-center justify-center gap-3 p-3 text-center text-mom-black hover:bg-[#F4F1E8]',
                    selectedIndex === totalItems - 1 && 'bg-[#F4F1E8]',
                  )}
                  href={`/search?query=${encodeURIComponent(inputValue)}`}
                >
                  {t('Search.viewMoreResults')}
                </Link>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default function SearchWrapper({
  priceConfig,
  anonymousUserToken,
  isLoggedIn,
}: {
  priceConfig: PriceConfig;
  anonymousUserToken: string;
  isLoggedIn?: boolean;
}) {
  return (
    <ErrorBoundary>
      <InstantSearch
        future={algoliaFutureSettings}
        indexName={env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME}
        insights={{
          insightsInitParams: {
            userToken: anonymousUserToken,
          },
        }}
        searchClient={customSearchClient}
      >
        <Configure
          analytics={true}
          analyticsTags={['auto-complete']}
          clickAnalytics={true}
          filters={`NOT ${algoliaFieldNames.IS_DRAM}:'1'`}
          hitsPerPage={6}
        />
        <AutocompleteSearch isLoggedIn={isLoggedIn} priceConfig={priceConfig} />
        <Index indexName={env.NEXT_PUBLIC_ALGOLIA_SUGGESTIONS_INDEX}>
          <Configure hitsPerPage={4} />
        </Index>
      </InstantSearch>
    </ErrorBoundary>
  );
}
